import {
  ChartAdapter,
  ChartBuildContext,
  EChartsOption,
  ScatterChartData,
  ScatterChartSettings,
} from "../types";

import {
  buildLegend,
  buildTooltip,
  buildVisualMap,
  mergeScatterSettings,
} from "./shared";

function extractValueStats(series: ScatterChartData["series"]) {
  let min: number | undefined;
  let max: number | undefined;

  series.forEach((seriesItem) => {
    seriesItem.data.forEach((point) => {
      const value = point.value ?? point.size;
      if (value === undefined || value === null) {
        return;
      }
      if (min === undefined || value < min) {
        min = value;
      }
      if (max === undefined || value > max) {
        max = value;
      }
    });
  });

  return { min, max };
}

function computeRegressionLine(points: ScatterChartData["series"][number]["data"]) {
  const validPoints = points
    .map((point) => ({ x: point.x, y: point.y }))
    .filter((point) =>
      typeof point.x === "number" && typeof point.y === "number"
    );

  const count = validPoints.length;
  if (count < 2) {
    return null;
  }

  const sums = validPoints.reduce(
    (acc, point) => {
      acc.sumX += point.x;
      acc.sumY += point.y;
      acc.sumXY += point.x * point.y;
      acc.sumX2 += point.x * point.x;
      return acc;
    },
    { sumX: 0, sumY: 0, sumXY: 0, sumX2: 0 }
  );

  const denominator = count * sums.sumX2 - sums.sumX * sums.sumX;
  if (denominator === 0) {
    return null;
  }

  const slope =
    (count * sums.sumXY - sums.sumX * sums.sumY) / denominator;
  const intercept = (sums.sumY - slope * sums.sumX) / count;
  const xs = validPoints.map((point) => point.x);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);

  return {
    x1: minX,
    y1: slope * minX + intercept,
    x2: maxX,
    y2: slope * maxX + intercept,
  };
}

const scatterChartAdapter: ChartAdapter<"scatter"> = {
  type: "scatter",
  buildOption: (ctx: ChartBuildContext<"scatter">) => {
    const { data } = ctx;
    const mergedSettings = mergeScatterSettings(ctx.settings);

    const { min, max } = extractValueStats(data.series);

    const xAxis = {
      type: data.xAxis?.axisType ?? "value",
      name: data.xAxis?.name,
      min: data.xAxis?.min,
      max: data.xAxis?.max,
      axisLabel: data.xAxis?.axisLabel,
      axisLine:
        data.xAxis?.showAxisLine === false
          ? { show: false }
          : data.xAxis?.showAxisLine === true
          ? { show: true }
          : undefined,
    };

    const yAxis = {
      type: data.yAxis?.axisType ?? "value",
      name: data.yAxis?.name,
      min: data.yAxis?.min,
      max: data.yAxis?.max,
      axisLabel: data.yAxis?.axisLabel,
      axisLine:
        data.yAxis?.showAxisLine === false
          ? { show: false }
          : data.yAxis?.showAxisLine === true
          ? { show: true }
          : undefined,
    };

    const legend = buildLegend(data.legend, mergedSettings.legend);
    const tooltip = buildTooltip(data.tooltip, {
      trigger: mergedSettings.tooltip?.trigger ?? "item",
      enable: mergedSettings.tooltip?.enable,
      formatter: mergedSettings.tooltip?.formatter,
    });

    const visualMap = buildVisualMap(
      data.visualMap,
      mergedSettings.visualMap,
      { min, max }
    );

    const series = data.series.map((seriesItem) => {
      const regression = mergedSettings.showRegressionLine
        ? computeRegressionLine(seriesItem.data)
        : null;

      return {
        type: "scatter",
        name: seriesItem.name,
        symbol: seriesItem.symbol ?? "circle",
        symbolSize: seriesItem.symbolSize ?? 12,
        data: seriesItem.data.map((point) => ({
          value: [point.x, point.y, point.value ?? point.size ?? null],
          name: point.name,
          symbolSize: point.size,
          itemStyle: point.color ? { color: point.color } : undefined,
        })),
        itemStyle: seriesItem.color ? { color: seriesItem.color } : undefined,
        emphasis: { focus: "series" },
        markLine: regression
          ? {
              symbol: "none",
              lineStyle: { type: "dashed" },
              data: [
                [
                  { coord: [regression.x1, regression.y1] },
                  { coord: [regression.x2, regression.y2] },
                ],
              ],
            }
          : undefined,
      };
    });

    const option: EChartsOption = {
      xAxis,
      yAxis,
      legend,
      tooltip,
      series,
    };

    if (visualMap) {
      option.visualMap = visualMap;
    }

    return option;
  },
  getDefaultSettings: (): Partial<ScatterChartSettings> => ({
    legend: { show: true, position: "top" },
    tooltip: { enable: true, trigger: "item" },
    visualMap: { show: false, calculable: true },
    showRegressionLine: false,
  }),
};

export default scatterChartAdapter;
